import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from '../../hooks/useTranslation';

const SEOHead = ({ 
  title, 
  description, 
  keywords, 
  canonical,
  ogImage,
  ogType = 'website'
}) => {
  const { currentLanguage, getPathWithLanguage, getPathWithoutLanguage, availableLanguages } = useLanguage();
  const { t } = useTranslation();

  const baseUrl = window.location.origin;
  const currentPath = window.location.pathname;
  const pathWithoutLang = getPathWithoutLanguage(currentPath) || '/';

  // Generate hreflang URLs
  const hreflangs = availableLanguages
    .filter((lang) => !['hi', 'ur'].includes(lang))
    .map((lang) => ({ lang, url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, lang)}` }))
    .concat([{ lang: 'x-default', url: `${baseUrl}${getPathWithLanguage(pathWithoutLang, 'ba')}` }]);

  const canonicalUrl = canonical || `${baseUrl}${getPathWithLanguage(pathWithoutLang, currentLanguage)}`;
  const finalTitle = title || t('seo.homeTitle');
  const finalDescription = description || t('seo.homeDescription');
  const finalKeywords = keywords || t('seo.homeKeywords');

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={currentLanguage} />
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <meta name="keywords" content={finalKeywords} />
      <meta name="robots" content="index, follow" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Hreflang Tags */}
      {hreflangs.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:locale" content={
        currentLanguage === 'ba' ? 'bs_BA' :
        currentLanguage === 'en' ? 'en_US' :
        currentLanguage === 'fr' ? 'fr_FR' :
        currentLanguage === 'es' ? 'es_ES' :
        currentLanguage === 'it' ? 'it_IT' :
        currentLanguage === 'ru' ? 'ru_RU' :
        currentLanguage === 'ar' ? 'ar_AR' :
        currentLanguage === 'de' ? 'de_DE' : 'bs_BA'
      } />
      {currentLanguage !== 'ba' && <meta property="og:locale:alternate" content="bs_BA" />}
      {currentLanguage !== 'en' && <meta property="og:locale:alternate" content="en_US" />}
      {currentLanguage !== 'fr' && <meta property="og:locale:alternate" content="fr_FR" />}
      {currentLanguage !== 'es' && <meta property="og:locale:alternate" content="es_ES" />}
      {currentLanguage !== 'it' && <meta property="og:locale:alternate" content="it_IT" />}
      {currentLanguage !== 'ru' && <meta property="og:locale:alternate" content="ru_RU" />}
      {currentLanguage !== 'ar' && <meta property="og:locale:alternate" content="ar_AR" />}
      {currentLanguage !== 'de' && <meta property="og:locale:alternate" content="de_DE" />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      
      {/* Language and Region */}
      <meta name="language" content={currentLanguage} />
      <meta name="geo.region" content="BA" />
      <meta name="geo.country" content="Bosnia and Herzegovina" />
    </Helmet>
  );
};

export default SEOHead;
